/**
 * Прогресс генерации: SSE как основной канал, поллинг раз в 2 секунды как
 * запасной.
 *
 * EventSource не умеет ставить заголовок Authorization, а контракт требует
 * Bearer-токен, поэтому поток читается через fetch + ReadableStream и
 * разбирается вручную. Если поток не открылся или трижды оборвался, watcher
 * молча переходит на GET /projects/:id раз в 2 секунды; о смене канала
 * сообщает onMode, чтобы экран честно писал, что происходит.
 *
 * Туннель Cloudflare может держать поток, не отдавая ни байта, а iOS рвёт
 * такой запрос только через минуту. Поэтому первый кусок данных ждём недолго
 * и, если его нет, уходим на опрос сразу: повторять бессмысленно.
 */
import type { ProjectStatus } from '@reels/shared';
import { ApiError, api, apiBaseUrl, authToken, isDemo } from './api';
import type { DoneEventData, FailedEventData, ProgressEventData, ProjectDetail } from './types';

export type StreamMode = 'sse' | 'poll';

export interface WatchHandlers {
  onProgress?: (data: ProgressEventData) => void;
  onDone?: (data: DoneEventData) => void;
  onFailed?: (data: FailedEventData) => void;
  onSnapshot?: (project: ProjectDetail) => void;
  onMode?: (mode: StreamMode) => void;
  onError?: (message: string) => void;
}

const POLL_INTERVAL_MS = 2000;
const SSE_MAX_ATTEMPTS = 3;
/** Сколько ждём первый байт потока, прежде чем считать его непроходимым. */
const SSE_FIRST_BYTE_MS = 8000;

const TERMINAL: ProjectStatus[] = ['done', 'failed', 'expired'];

export function isTerminal(status: ProjectStatus): boolean {
  return TERMINAL.includes(status);
}

interface ParsedEvent {
  event: string;
  data: string;
}

function parseBlock(block: string): ParsedEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

/**
 * Следит за проектом до конечного статуса. Возвращает функцию остановки.
 */
export function watchProject(projectId: string, handlers: WatchHandlers): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let pollTimer: number | null = null;

  const stop = (): void => {
    stopped = true;
    controller?.abort();
    controller = null;
    if (pollTimer !== null) {
      window.clearTimeout(pollTimer);
      pollTimer = null;
    }
  };

  const handleEvent = (parsed: ParsedEvent): boolean => {
    let payload: unknown;
    try {
      payload = JSON.parse(parsed.data);
    } catch {
      return false;
    }
    if (parsed.event === 'progress') {
      handlers.onProgress?.(payload as ProgressEventData);
      return false;
    }
    if (parsed.event === 'done') {
      handlers.onDone?.(payload as DoneEventData);
      return true;
    }
    if (parsed.event === 'failed') {
      handlers.onFailed?.(payload as FailedEventData);
      return true;
    }
    return false;
  };

  const poll = async (): Promise<void> => {
    if (stopped) return;
    handlers.onMode?.('poll');
    try {
      const project = await api.getProject(projectId);
      if (stopped) return;
      handlers.onSnapshot?.(project);
      if (isTerminal(project.status)) {
        stop();
        return;
      }
    } catch (error) {
      if (stopped) return;
      if (error instanceof ApiError && error.code === 'not_found') {
        handlers.onError?.(error.message);
        stop();
        return;
      }
      // Сеть моргнула — продолжаем опрашивать.
    }
    if (!stopped) pollTimer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
  };

  const stream = async (): Promise<void> => {
    for (let attempt = 1; attempt <= SSE_MAX_ATTEMPTS; attempt += 1) {
      if (stopped) return;
      controller = new AbortController();
      const current = controller;
      let gotBytes = false;
      const firstByteTimer = window.setTimeout(() => {
        if (!gotBytes) current.abort();
      }, SSE_FIRST_BYTE_MS);
      try {
        const token = authToken();
        const response = await fetch(`${apiBaseUrl}/projects/${projectId}/events`, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error('stream unavailable');

        handlers.onMode?.('sse');
        // Догоняем текущее состояние: события могли начаться до подписки.
        void api
          .getProject(projectId)
          .then((project) => {
            if (!stopped) handlers.onSnapshot?.(project);
          })
          .catch(() => undefined);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          gotBytes = true;
          window.clearTimeout(firstByteTimer);
          buffer += decoder.decode(value, { stream: true });
          let separator = buffer.indexOf('\n\n');
          while (separator !== -1) {
            const block = buffer.slice(0, separator);
            buffer = buffer.slice(separator + 2);
            const parsed = parseBlock(block);
            if (parsed && handleEvent(parsed)) {
              stop();
              return;
            }
            separator = buffer.indexOf('\n\n');
          }
        }
        if (stopped) return;
        // Поток закрылся без терминального события — пробуем переподключиться.
      } catch {
        if (stopped) return;
      } finally {
        window.clearTimeout(firstByteTimer);
      }
      // Ни байта за отведённое время: канал не работает, сразу на опрос.
      if (!gotBytes) break;
      await new Promise((resolve) => window.setTimeout(resolve, 1000 * 2 ** (attempt - 1)));
    }
    if (!stopped) void poll();
  };

  if (isDemo()) void poll();
  else void stream();

  return stop;
}
